import type { BrandAuthConfig, BrandConnectionKey } from "@brand-auth/types";

/**
 * Subset of the Kinde React SDK's `register` function signature (v5 of
 * `@kinde-oss/kinde-auth-react`). Accepted as a parameter so the helper
 * stays trivially testable and never reaches into the SDK at module scope.
 *
 * v5 uses the flat `LoginMethodParams` shape (`connectionId`, `orgCode`,
 * `loginHint`) — v4's nested `authUrlParams: { connection_id, org_code }`
 * was removed.
 */
export type KindeRegisterFn = (params: {
  connectionId?: string;
  orgCode?: string;
  loginHint?: string;
}) => Promise<void> | void;

/**
 * Trigger Kinde signup using the brand's connection + org code.
 *
 * Always passes `orgCode` even though Kinde Applications can have a default
 * org configured; relying on the default has historically dropped on Apple /
 * Discord and silently routes the new user into the wrong tenant. See
 * docs/PLAN_SATELLITE_AUTH.md §10 (Org assignment drift).
 *
 * Optional `loginHint` is forwarded to Kinde so social providers can
 * pre-select the matching account when the user has already typed their
 * email earlier in the funnel (PLAN_CUSTOM_AUTH_FLOW.md §4 — social path).
 * We omit the field entirely when not supplied so the request stays
 * byte-identical to the pre-`loginHint` behavior; some Kinde providers
 * trip on `loginHint=""` instead of treating it as absent.
 */
export const registerWithConnection = async (params: {
  config: BrandAuthConfig;
  connection: BrandConnectionKey;
  register: KindeRegisterFn;
  loginHint?: string;
}): Promise<void> => {
  const { config, connection, register, loginHint } = params;
  const connectionId = config.connectionIds[connection];
  const trimmedHint = loginHint?.trim();
  await register({
    connectionId,
    orgCode: config.kindeOrgCode,
    ...(trimmedHint ? { loginHint: trimmedHint } : {}),
  });
};
